import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { Menubar } from 'primereact/menubar';
import { OrderService } from './service/OrderService';
import OrderViewEdit from './subcomponents/OrderViewEdit';
import { DataView } from 'primereact/dataview';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import './Orders.css';
import { formatDateLong, formatDateWeekday, generateOrderDescription } from '../utils.js';
import { add, startOfWeek, endOfWeek, eachWeekOfInterval, isWithinInterval, formatISO, parseISO, isSameWeek, format } from 'date-fns';
import PotentialInvoices from './subcomponents/PotentialInvoices';
import AnalyticsPage from './subcomponents/AnalyticsPage';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import CustomerExplorerModal from './subcomponents/CustomerExplorerModal';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { Dialog } from 'primereact/dialog';


const initializeDateRange = () => {
  const defaultDateRange = {
    start: new Date().toISOString(),
    end: add(new Date(), { days: 90 }).toISOString(),
  };

  try {
    const storedDateRange = localStorage.getItem('dateRange_');
    if (!storedDateRange) return defaultDateRange;

    const parsed = JSON.parse(storedDateRange);
    const start = new Date(parsed.start);
    const end = new Date(parsed.end);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) throw new Error('Invalid dates');
    return parsed;
  } catch (error) {
    console.warn('Invalid dateRange in localStorage, using default:', error);
    return defaultDateRange;
  }
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [productProperties, setProductProperties] = useState({});
  const [orderProperties, setOrderProperties] = useState({});
  const [employees, setEmployees] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderInvoice, setSelectedOrderInvoice] = useState(null);
  const [editable, setEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  // New state for collapsible left menu
  const [isLeftMenuCollapsed, setIsLeftMenuCollapsed] = useState(false);
  const [isBackdropActive, setIsBackdropActive] = useState(false);

  const toast = useRef(null);


  // State variable for search term
  const [searchTerm, setSearchTerm] = useState("");
  // State variable for filtered orders
  const [filteredOrders, setFilteredOrders] = useState([]);

  const toggleLeftMenu = () => {
    setIsLeftMenuCollapsed(!isLeftMenuCollapsed);
    setIsBackdropActive(!isLeftMenuCollapsed);
  };

  // --- Invoice status normalization (handles "Not Sent" vs "NOT_SENT", etc) ---
  const normInvoiceStatus = (s) =>
    String(s ?? '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');

  // What you want to be able to filter by (edit labels as you like)
  const INVOICE_STATUS_OPTIONS = [
    { key: 'PARTIALLY_PAID', label: 'Partially Paid' },
    { key: 'UNPAID', label: 'Unpaid' },
    { key: 'ONLINE', label: 'Online' },
    { key: 'COMP', label: 'Comp' },
    { key: 'PARTIALLY_REFUNDED', label: 'Partially Refunded' },
    { key: 'CANCELED', label: 'Canceled' },
    { key: 'NOT_SENT', label: 'Not Sent / Not Linked' },
    { key: 'PAID', label: 'Paid' },
  ];

  // --- Filters state ---
  const [deliveryOnly, setDeliveryOnly] = useState(false);
  const [notPaidOnly, setNotPaidOnly] = useState(false);

  // multi-status OR group (ignored when notPaidOnly=true)
  const [invoiceStatusFilters, setInvoiceStatusFilters] = useState(() => {
    const init = {};
    INVOICE_STATUS_OPTIONS.forEach((o) => (init[o.key] = false));
    return init;
  });

    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  const activeAdvancedFilterCount = useCallback(() => {
    const statusCount = Object.values(invoiceStatusFilters).filter(Boolean).length;
    return (deliveryOnly ? 1 : 0) + (notPaidOnly ? 1 : 0) + statusCount;
  }, [deliveryOnly, notPaidOnly, invoiceStatusFilters]);


  const selectedInvoiceStatusKeys = useCallback(() => {
    return Object.entries(invoiceStatusFilters)
      .filter(([, v]) => !!v)
      .map(([k]) => k);
  }, [invoiceStatusFilters]);

  const clearStatusFilters = useCallback(() => {
    setDeliveryOnly(false);
    setNotPaidOnly(false);
    setInvoiceStatusFilters((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => (next[k] = false));
      return next;
    });
  }, []);


  // State variables for date range
  const defaultDateRange = {
    start: new Date().toISOString(),
    end: add(new Date(), { days: 90 }).toISOString()
  };

  const storedDateRange = localStorage.getItem('dateRange_');
  const initialDateRange = storedDateRange
    ? (() => {
      try {
        const parsed = JSON.parse(storedDateRange);
        const start = new Date(parsed.start);
        const end = new Date(parsed.end);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          throw new Error('Invalid dates');
        }
        return parsed;
      } catch (error) {
        console.warn('Invalid dateRange in localStorage, using defaultDateRange:', error);
        return defaultDateRange;
      }
    })()
    : defaultDateRange;


  const [dateRange, setDateRange] = useState(() => {
    const initialRange = initializeDateRange();
    return {
      start: new Date(initialRange.start),
      end: new Date(initialRange.end),
    };
  });

  useEffect(() => {
    const storedDateRange = {
      start: dateRange.start.toISOString(),
      end: dateRange.end.toISOString()
    };
    localStorage.setItem('dateRange_', JSON.stringify(storedDateRange));
  }, [dateRange]);

  // useEffect(() => {
  //   const storedSelectedOrder = localStorage.getItem('orders_selectedOrder');
  //   const initialSelectedOrder = storedSelectedOrder
  //     ? JSON.parse(storedSelectedOrder)
  //     : null;

  //   setSelectedOrder(initialSelectedOrder);
  // }, []);

  // useEffect(() => {
  //   if (selectedOrder !== null) {
  //     localStorage.setItem('orders_selectedOrder', JSON.stringify(selectedOrder));
  //   } else {
  //     localStorage.removeItem('orders_selectedOrder');
  //   }

  //   console.log("selectedOrder: ", selectedOrder);
  //   console.log("storage selectedOrder: ", JSON.parse(localStorage.getItem('orders_selectedOrder')));
  // }, [selectedOrder]);
  useEffect(() => {
    localStorage.removeItem('orders_selectedOrder');
  }, [])


  useEffect(() => {
    setIsRefreshing(true);
    Promise.all([
      OrderService.getProductProperties().then(setProductProperties),
      refreshOrders(),
      OrderService.getEmployees().then(setEmployees),
      OrderService.getOrderProperties().then(setOrderProperties),
      OrderService.getSquareInvoices().then(setInvoices),
    ]).finally(() => setIsRefreshing(false));
  }, []);

  useEffect(() => {
    //console.log("MAIN: Product Properties: ", productProperties);
  }, [productProperties]);


  const refreshAllData = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        OrderService.getProductProperties().then(setProductProperties),
        refreshOrders(),
        OrderService.getEmployees().then(setEmployees),
        OrderService.getOrderProperties().then(setOrderProperties),
        OrderService.getSquareInvoices().then(setInvoices),
      ]);
      //console.log("All data refreshed successfully.");
    } catch (error) {
      console.error("Error refreshing all data:", error);
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to refresh all data.' });
    } finally {
      setIsRefreshing(false);
    }
  };


  const [consolidatedInvoices, setConsolidatedInvoices] = useState([]);
  const [missingInvoices, setMissingInvoices] = useState([]);
  const [invoicesNotConsolidated, setInvoicesNotConsolidated] = useState([]);

  useEffect(() => {
    const _consolidatedInvoices = [];
    const _missingInvoices = [];
    const _invoicesNotConsolidated = [...invoices]; // Initially set it as all invoices

    orders.forEach(order => {
      const orderName = order.customer.name;
      const orderEmail = order.customer.email;

      // Try to find the corresponding invoice
      const matchingInvoice = invoices.find(invoice => {
        const invoiceName = `${invoice?.primaryRecipient?.givenName} ${invoice?.primaryRecipient?.familyName}`;
        const invoiceEmail = invoice?.primaryRecipient?.emailAddress;

        return invoiceName === orderName || invoiceEmail === orderEmail;
      });

      if (matchingInvoice) {
        _consolidatedInvoices.push({
          ...order,
          invoice: matchingInvoice
        });

        // Remove the invoice from invoicesNotConsolidated
        const index = _invoicesNotConsolidated.findIndex(invoice => invoice.id === matchingInvoice.id);
        if (index !== -1) {
          _invoicesNotConsolidated.splice(index, 1);
        }
      } else {
        _missingInvoices.push(order);
      }
    });

    setConsolidatedInvoices(_consolidatedInvoices);
    setMissingInvoices(_missingInvoices);
    setInvoicesNotConsolidated(_invoicesNotConsolidated);
    //console.log("invoices", invoices)
  }, [orders, invoices]);



  useEffect(() => {
  let tempOrders = orders;

  // Search filter
  if (searchTerm !== "") {
    tempOrders = tempOrders.filter(order =>
      order.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.customer.phone && order.customer.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.customer.email && order.customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.name && order.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.orderID && order.orderID.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.dueDate && formatDateLong(order.dueDate).toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.deliveryAddress && order.deliveryAddress.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.recipient.name && order.recipient.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.squareCustomerID && order.squareCustomerID.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }

  // Date range filter
  tempOrders = tempOrders.filter(order => {
    const orderDueDate = new Date(order.dueDate);
    if (isNaN(orderDueDate.getTime())) {
      console.warn(`Invalid dueDate for order ${order._id}: ${order.dueDate}`);
      return false;
    }
    return orderDueDate >= dateRange.start && orderDueDate <= dateRange.end;
  });

  // ✅ Delivery AND filter
  if (deliveryOnly) {
    tempOrders = tempOrders.filter(o => String(o?.type || '') === 'Delivery');
  }

  // ✅ Invoice status filters:
  // - if "Not Paid" checked -> everything except PAID
  // - else if specific statuses checked -> OR those statuses
  if (notPaidOnly) {
    tempOrders = tempOrders.filter(o => normInvoiceStatus(o?.invoiceStatus) !== 'PAID');
  } else {
    const keys = Object.entries(invoiceStatusFilters)
      .filter(([, v]) => !!v)
      .map(([k]) => k);

    if (keys.length > 0) {
      tempOrders = tempOrders.filter(o => keys.includes(normInvoiceStatus(o?.invoiceStatus)));
    }
  }

  setFilteredOrders(tempOrders);
}, [searchTerm, orders, dateRange, deliveryOnly, notPaidOnly, invoiceStatusFilters]);



  const [squareCustomers, setSquareCustomers] = useState([]);


  useEffect(() => {
    refreshOrders();
  }, [dateRange]);

  const refreshOrders = async () => {
    setIsRefreshing(true);
    try {
      // Validate dateRange before proceeding
      if (
        !dateRange.start ||
        isNaN(dateRange.start.getTime()) ||
        !dateRange.end ||
        isNaN(dateRange.end.getTime())
      ) {
        throw new Error('Invalid date range selected.');
      }

      // Optionally, ensure that start date is before end date
      if (dateRange.start > dateRange.end) {
        throw new Error('Start date cannot be after end date.');
      }

      const fetchedOrders = await OrderService.getOrdersByDateRange(dateRange.start, dateRange.end);
      fetchedOrders.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
      setOrders(fetchedOrders);
      setFilteredOrders(fetchedOrders);
      //console.log("Orders fetched and set:", fetchedOrders);

      const fetchedSquareCustomers = await OrderService.getSquareCustomers();
      setSquareCustomers(fetchedSquareCustomers);
    } catch (error) {
      console.error("Error refreshing orders:", error);
      toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to refresh orders: ${error.message}` });
    } finally {
      setIsRefreshing(false);
    }
  };


  const [filteredPhoneNumbers, setFilteredPhoneNumbers] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);

  useEffect(() => {
    const tempPhoneNumbers = [];
    const tempEmails = [];
    orders.forEach(order => {
      if (order.customer.phone && order.customer.phone.trim() !== "") {
        tempPhoneNumbers.push({ phone: order.customer.phone });
      }
      if (order.customer.email && order.customer.email.trim() !== "") {
        tempEmails.push({ email: order.customer.email });
      }
    });
    // Remove duplicates
    const uniquePhoneNumbers = tempPhoneNumbers.filter((v, i, a) => a.findIndex(t => (t.phone === v.phone)) === i);
    const uniqueEmails = tempEmails.filter((v, i, a) => a.findIndex(t => (t.email === v.email)) === i);

    // Process phone numbers
    uniquePhoneNumbers.forEach(phoneNumber => {
      if (phoneNumber.phone && phoneNumber.phone.length === 12) {
        if (phoneNumber.phone.charAt(0) === "+") {
          phoneNumber.phone = phoneNumber.phone.substring(2);
        } else if (phoneNumber.phone.charAt(0) === "1") {
          phoneNumber.phone = phoneNumber.phone.substring(1);
        }
      } else if (phoneNumber.phone && phoneNumber.phone.length === 11 && phoneNumber.phone.charAt(0) === "1") {
        phoneNumber.phone = phoneNumber.phone.substring(1);
      }
      // No need to handle length 10 case as "do nothing" is implied
    });

    setFilteredPhoneNumbers(uniquePhoneNumbers);
    setFilteredEmails(uniqueEmails);

  }, [orders]);



  const handleOrderClick = async (order) => {
    if (isRefreshing || isSaving) {
      return;
    }

    // Verify that the selected order exists in the current orders list
    const exists = orders.find(o => o._id === order._id);
    if (!exists) {
      console.warn(`Order with ID ${order._id} does not exist.`);
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Selected order no longer exists.' });
      return;
    }

    setSelectedOrder(order);
    if (order.squareInvoiceID && order.squareInvoiceID !== "") {
      try {
        const invoiceData = await OrderService.getSquareInvoice(order.squareInvoiceID);
        //console.log("Fetched invoiceData: ", invoiceData);
        setSelectedOrderInvoice(invoiceData);
        if (invoiceData.invoice.status !== order.invoiceStatus
          || (invoiceData.invoice.primaryRecipient.givenName + " " + invoiceData.invoice.primaryRecipient.familyName) !== order.customer.name || invoiceData.invoice.primaryRecipient.emailAddress !== order.customer.email //|| invoiceData.invoice.primaryRecipient.phoneNumber !== order.customer.phone
        ) {
          const updatedOrder = {
            ...order,
            invoiceStatus: invoiceData.invoice.status, // Replace 'status' with the correct property name from the invoiceData
            customer: {
              ...order.customer,
              name: invoiceData.invoice.primaryRecipient.givenName + " " + invoiceData.invoice.primaryRecipient.familyName,
              email: invoiceData.invoice.primaryRecipient.emailAddress,
              phone: invoiceData?.invoice?.primaryRecipient?.phoneNumber ? invoiceData.invoice.primaryRecipient.phoneNumber : order.customer.phone
            }
          };
          //console.log("updatedOrder: ", updatedOrder)
          // You might also want to call some function to update the order in your back end.
          const savedOrder = await OrderService.updateOrder(updatedOrder._id, updatedOrder);
          refreshOrders();
          setSelectedOrder(savedOrder);
        }
      } catch (error) {
        console.error("Error fetching latest invoice status:", error);

      }
    }
    setEditable(false);
  };

  const getSquareInvoice = async (invoiceID) => {
    try {
      const invoiceData = await OrderService.getSquareInvoice(invoiceID);
      return invoiceData;
    } catch (error) {
      console.error("Error fetching latest invoice status:", error);
    }
  };

  const searchSquareCustomer = async (email, phone) => {
    try {
      const customerData = await OrderService.searchSquareCustomer(email, phone);
      return customerData;
    } catch (error) {
      console.error("Error fetching latest customer data:", error);
    }
  };


  const openNewOrder = () => {
    const newOrder = getDefaultOrder();
    //console.log('New Order: ', newOrder);
    setSelectedOrder(newOrder);
    setEditable(true);
  };


  const getDefaultOrder = () => ({
    name: '',
    description: '',
    price: 0,
    type: '',
    quantity: 1,
    orderStatus: 'New Order',
    invoiceStatus: 'Not Sent',
    priority: 0,
    customer: {
      name: '',
      phone: '',
      email: '',
      contactDate: new Date(),
      internal_notes: '',
    },
    dueDate: new Date(),
    scheduledTime: '',
    // scheduledTimeEnd: '',
    deliveryAddress: '',
    deliveryFee: 0,
    budget: 0,
    occasion: '',
    theme: '',
    servings: 0,
    photoAttached: false,
    photo: '',
    recipient: {
      name: '',
      age: 0,
    },
    orderItems: [{
      cakes: [],
      cupcakes: [],
      cakePops: [],
      cookies: [],
      generalItems: [],
    }],
    attachments: [],
    squareCustomerID: '',
    squareInvoiceID: '',
    orderID: '',
    additionalFees: [],
    discounts: [],
  });


  const openEditOrder = () => {
    if (selectedOrder) {
      // only open if an order is selected
      setEditable(!editable);
    }
  };

  function deepCloneAndRemoveIds(object) {
    if (typeof object !== 'object' || object === null) {
      // object is a primitive type or null, so just return it
      return object;
    }

    if (Array.isArray(object)) {
      // object is an array, clone each item in the array
      return object.map(deepCloneAndRemoveIds);
    }

    // object is an object, clone each property
    let clonedObject = {};
    for (let key in object) {
      if (key !== '_id' && key !== 'squareInvoiceID' &&
        key !== 'squareCustomerID' && key !== 'invoiceStatus' && key !== 'orderStatus'
      ) { // ignore _id keys
        clonedObject[key] = deepCloneAndRemoveIds(object[key]);
      }
    }

    // Set default values for duplicated order
    clonedObject.invoiceStatus = "Not Sent";
    clonedObject.orderStatus = "New Order";

    // Ensure photos array is removed or reset
    if (object.photos) {
      clonedObject.photos = []; // Reset the photos array to an empty array
      clonedObject.photoAttached = false; // Reset related flags
    }

    return clonedObject;
  }


  const copyOrder = () => {
    if (selectedOrder) {
      //console.log("copying order: ", selectedOrder);
      let tempOrder = deepCloneAndRemoveIds(selectedOrder); // copy the order and remove all _id fields
      //console.log("copied order: ", tempOrder);
      setSelectedOrder(tempOrder);
      setEditable(true);
    }
  };

  // const deleteOrder = () => {
  //   setIsSaving(true);
  //   if (selectedOrder) {
  //     if (window.confirm("Confirm you wish to delete this order")) {
  //       OrderService.deleteOrder(selectedOrder._id)
  //         .then(() => {
  //           setOrders(orders.filter((order) => order._id !== selectedOrder._id));
  //           setSelectedOrder(null);
  //         })
  //         .catch((err) => console.error(err));
  //     }
  //   }
  //   setIsSaving(false);
  // };
  const deleteOrder = async () => {
    if (!selectedOrder) return;

    const confirmDelete = window.confirm("Are you sure you want to delete this order?");
    if (!confirmDelete) return;

    setIsSaving(true);

    try {
      // Delete images from Cloudinary
      if (selectedOrder.photos && selectedOrder.photos.length > 0) {
        for (const photo of selectedOrder.photos) {
          const urlParts = photo.url.split('/');
          const publicId = urlParts[urlParts.length - 1].split('.')[0]; // Extract public ID
          await OrderService.deleteImageFromCloudinary(publicId); // Call to your service to delete the image
        }
      }

      // Delete the order from MongoDB
      await OrderService.deleteOrder(selectedOrder._id);

      toast.current.show({ severity: 'success', summary: 'Success', detail: 'Order and associated images deleted successfully.' });

      setSelectedOrder(null);
      await refreshOrders(); // Refresh orders to update the UI
    } catch (error) {
      console.error("Error deleting order or associated images:", error);
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete the order or associated images.' });
    } finally {
      setIsSaving(false);
    }
  };

  const [viewAll, setViewAll] = useState(false);

  const items = [
    {
      label: 'New',
      icon: 'pi pi-fw pi-file',
      command: openNewOrder,
      disabled: isRefreshing || isResyncing || isSaving,
    },
    {
      label: 'Edit',
      icon: 'pi pi-fw pi-pencil',
      command: openEditOrder,
      disabled: isRefreshing || isResyncing || isSaving,
    },
    {
      label: 'Duplicate',
      icon: 'pi pi-fw pi-clone',
      command: copyOrder,
      disabled: isRefreshing || isResyncing || isSaving,
    },
    {
      label: 'Delete',
      icon: 'pi pi-fw pi-trash',
      command: deleteOrder,
      //disabled: isRefreshing || isResyncing,
      disabled: isRefreshing || isResyncing || isSaving,
    },
    {
      label: 'Complete',
      icon: 'pi pi-fw pi-check',
      command: () => updateStatus('Completed'),
      disabled: isRefreshing || isResyncing || isSaving,
    },
    // {
    //   label: 'Archive',
    //   icon: 'pi pi-fw pi-lock',
    //   command: () => updateStatus('Archived'),
    //   disabled: isRefreshing || isResyncing,
    // },
    {
      label: 'Refresh All Data',
      icon: isRefreshing ? "pi pi-spin pi-refresh" : "pi pi-refresh",
      disabled: isRefreshing || isResyncing || isSaving,
      command: refreshAllData,
    },
    {
      label: 'Resync Invoices',
      icon: isResyncing ? "pi pi-spin pi-refresh" : "pi pi-refresh",
      disabled: isResyncing || isRefreshing || isSaving,
      command: async () => {
        try {
          setIsResyncing(true);
          setIsRefreshing(true);
          await OrderService.getSquareInvoices().then(setInvoices);
          if (filteredOrders) {
            for (const order of filteredOrders) {
              if (order.squareInvoiceID && order.squareInvoiceID !== "") {
                const invoiceData = await OrderService.getSquareInvoice(order.squareInvoiceID);

                if (invoiceData.invoice.status !== order.invoiceStatus) {
                  const updatedOrder = {
                    ...order,
                    invoiceStatus: invoiceData.invoice.status
                  };
                  //console.log("updatedOrder: ", updatedOrder);
                  const savedOrder = await OrderService.updateOrder(updatedOrder._id, updatedOrder);
                  //refreshOrders();

                }
              }
            }
            await refreshAllData();
          }
        } catch (error) {
          console.error("Error fetching latest invoice statuses:", error);
          setIsRefreshing(false);

        }
        setIsResyncing(false);
      }
    },
    {
      label: 'View All Invoices',
      icon: 'pi pi-fw pi-money-bill',
      command: () => {
        setViewAll(!viewAll);
      },
    },
    {
      label: "Resync Customers",
      icon: isResyncing ? "pi pi-spin pi-refresh" : "pi pi-refresh",
      disabled: isRefreshing || isResyncing,
      command: async () => {
        try {
          setIsResyncing(true);
          setIsRefreshing(true);
          if (filteredOrders) {
            for (const order of filteredOrders) {
              if (order.squareCustomerID && order.squareCustomerID !== "") {
                const customerData = await OrderService.getSquareCustomer(order.squareCustomerID);

                if ((customerData.customer.givenName + ' ' + customerData.customer.familyName) !== order.customer.name || customerData.customer.emailAddress !== order.customer.email || customerData.customer.phoneNumber !== order.customer.phone) {
                  const updatedOrder = {
                    ...order,
                    customer: {
                      ...order.customer,
                      name: customerData.customer.givenName + ' ' + customerData.customer.familyName,
                      email: customerData.customer.emailAddress,
                      phone: customerData.customer.phoneNumber
                    }
                  };
                  //console.log("Customer Sync updatedOrder: ", updatedOrder);
                  const savedOrder = await OrderService.updateOrder(updatedOrder._id, updatedOrder);
                  //console.log("Customer Sync savedOrder: ", savedOrder);

                }
              }
            }
            await refreshOrders();
          }

        } catch (error) {
          console.error("Error fetching latest customer data:", error);

        }
        setIsResyncing(false);
        setIsRefreshing(false);
      }
    },
    // {
    //   label: 'Prepare Invoice',
    //   icon: 'pi pi-fw pi-money-bill',
    //   command: prepareInvoice(selectedOrder),

    // }

  ];

  const updateStatus = async (status) => {
    if (window.confirm(`Confirm you wish to update this order status to: ${status}`)) {
      const updatedOrder = { ...selectedOrder, orderStatus: status };
      const savedOrder = await OrderService.updateOrder(updatedOrder._id, updatedOrder);
      //console.log('Saved Order:', savedOrder);

      setSelectedOrder(savedOrder); // reselects the order
      setEditable(false);
      refreshOrders();
    }
  };


  const orderTemplate = (order, index) => {
    // Check if the order exists in consolidatedInvoices
    const hasInvoice = consolidatedInvoices.some(consolidatedOrder => consolidatedOrder._id === order._id);

    let borderColor = "";
    let tagSeverity = "";
    let invoiceStatus = "";

    if (order.squareCustomerID && order.squareInvoiceID || order.invoiceStatus !== "Not Sent") {
      switch (order.invoiceStatus) {
        case "PARTIALLY_PAID":
          borderColor = "orange";
          tagSeverity = "warning";
          invoiceStatus = "Partially Paid";
          break;
        case "PAID":
          borderColor = "green";
          tagSeverity = "success";
          invoiceStatus = "Paid";
          break;
        case "ONLINE":
          borderColor = "green";
          tagSeverity = "success";
          invoiceStatus = "Online";
          break;
        case "COMP":
          borderColor = "green";
          tagSeverity = "success";
          invoiceStatus = "Comp";
          break;
        case "PARTIALLY_REFUNDED":
          borderColor = "orange";
          tagSeverity = "warning";
          invoiceStatus = "Partially Refunded";
          break;
        case "CANCELED":
          borderColor = "grey";
          tagSeverity = "info";
          invoiceStatus = "Canceled";
          break;
        case "UNPAID":
          borderColor = "red";
          tagSeverity = "danger";
          invoiceStatus = "Unpaid";
          break;
        default:
          borderColor = "blue"; // For any other invoice state
          tagSeverity = "info";
          invoiceStatus = "Not Linked";
      }
    }

    const hasFinishedProduct = Array.isArray(order.photos) &&
    order.photos.some(p => (p.tag || '').toLowerCase() === 'finished product');

    return (
      <>
        <div
          onClick={() => handleOrderClick(order)}
        className={`order-item ${hasFinishedProduct ? 'finished-product' : ''} ${selectedOrder && selectedOrder._id === order._id ? 'selected' : ''}`}
        style={{
          width: '100%',
          borderColor: borderColor,
          borderWidth: '2px',
          borderStyle: 'solid',
          borderRadius: '15px',
          padding: '8px',
          marginBottom: '2px'
        }}
        >

          <div className="order-details">
            <div className="order-customer"><strong>{order.customer.name}</strong></div>
            <div className="order-name"><strong>{(order.orderID ? order.orderID + ' ' : null)}</strong>{order.name}</div>
            {(order.customer.phone === '' || !order.customer.phone) && <Tag severity="danger" value="Missing Phone" rounded></Tag>}
            {(order.deliveryAddress === '' && order.type === 'Delivery') && <Tag severity="danger" value="Missing Delivery Address" rounded></Tag>}
          </div>


          {/* <div>
          {invoiceStatus && <Tag severity={tagSeverity} value={invoiceStatus} rounded></Tag>}
        </div> */}
          {/* {order.squareCustomerID && order.squareInvoiceID && (
          <i className="pi pi-money-bill" style={{ color: iconColor }}></i>
        )} */}

          {/* Add the Chip here based on hasInvoice or isMissingInvoice */}
          {/* {hasInvoice && <Chip label="Has Invoice" className="p-mr-2 p-mb-2" />}
        {isMissingInvoice && <Chip label="Missing Invoice" className="p-mr-2 p-mb-2" />} */}

          <div className="order-dates right-align">
            <span style={{ marginRight: '5px' }}>
              {((order.deliveryAddress !== '' && order.deliveryAddress) && order.type == 'Delivery') && <Tag severity="primary" value="Delivery" rounded></Tag>}
              {invoiceStatus && <Tag severity={tagSeverity} value={invoiceStatus} rounded></Tag>}
            </span>
            <span className="order-weekday">
              {formatDateWeekday(order.dueDate)}</span>
            <div className="order-date">{formatDateLong(order.dueDate)}</div>
          </div>

          {/* <OrderItem order={order} productProperties={productProperties} onPreviewClick={(e) => e.stopPropagation()} /> */}
        </div>
      </>
    );
  };

  const linkInvoiceToOrder = async (order, invoice) => {
    // Add the invoice ID and customer ID to the order
    const updatedOrder = {
      ...order,
      squareInvoiceID: invoice.id,
      squareCustomerID: invoice.primaryRecipient.customerId,
      invoiceStatus: invoice.status,
      customer: {
        ...order.customer,
        name: invoice.primaryRecipient.givenName + " " + invoice.primaryRecipient.familyName,
        email: invoice.primaryRecipient.emailAddress,
        phone: invoice.primaryRecipient.phoneNumber
      }
    };
    //console.log("updatedOrder: ", updatedOrder);
    try {
      const newOrder = await OrderService.updateOrder(updatedOrder._id, updatedOrder);
      //console.log("newOrder: ", newOrder);
      refreshOrders();
      const invoiceData = await OrderService.getSquareInvoice(newOrder.squareInvoiceID);
      setSelectedOrderInvoice(invoiceData);
      setSelectedOrder(newOrder);
      toast.current.show({ severity: 'success', summary: 'Success', detail: 'Invoice linked successfully' });
    } catch (error) {
      console.error("Failed to link invoice:", error);
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error linking invoice to order' });
    }
  };

  const unlinkInvoiceFromOrder = async (order) => {
    // Remove the invoice ID and customer ID from the order
    const updatedOrder = {
      ...order,
      squareInvoiceID: "",
      squareCustomerID: "",
      invoiceStatus: 'Not Sent',
    };
    //console.log("updatedOrder: ", updatedOrder);
    const newOrder = await OrderService.updateOrder(updatedOrder._id, updatedOrder);
    //console.log("newOrder: ", newOrder);
    refreshOrders();
    setSelectedOrder(newOrder);
  };


  return (
    <div className="orders-container" style={{ fontFamily: 'Arial, sans-serif', height: 'calc(100vh - 50px)', display: 'flex', flexDirection: 'row' }}>

      <div
        className={`left-menu ${isLeftMenuCollapsed ? 'collapsed' : 'expanded'}`}
        style={{
          flex: isLeftMenuCollapsed ? '0 0 0' : '1',
          display: 'flex',
          flexDirection: 'column',
          padding: '1em',
          borderRight: '1px solid #ccc',
          background: '#f9f9f9',
          transition: 'flex 0.3s ease, width 0.3s ease'
        }}
      >

        {/* Toggle Button */}
        {isLeftMenuCollapsed && <Button
          icon={isLeftMenuCollapsed ? 'pi pi-angle-double-right' : 'pi pi-angle-double-left'}
          onClick={toggleLeftMenu}
          className="p-button-text toggle-button"
          aria-label={isLeftMenuCollapsed ? 'Expand Menu' : 'Collapse Menu'}
          style={{
            alignSelf: 'flex-end', marginBottom: '1em',
            marginRight: !isLeftMenuCollapsed ? '0em' : '-1.25em',
          }}
        />}


        {/* Conditionally Render Left Menu Content */}
        {!isLeftMenuCollapsed && (
          <>
            <div style={{
              display: 'flex',
              direction: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h5 style={{ marginBottom: '10px' }}>Order Filters</h5>  <Button
                icon={isLeftMenuCollapsed ? 'pi pi-angle-double-right' : 'pi pi-angle-double-left'}
                onClick={toggleLeftMenu}
                className="p-button-text toggle-button"
                aria-label={isLeftMenuCollapsed ? 'Expand Menu' : 'Collapse Menu'}
                style={{
                  alignSelf: 'flex-end',
                  marginBottom: '1em',
                  transform: isLeftMenuCollapsed ? 'translateX(-0.5em)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              />
            </div>
            <InputText id="search" placeholder="Search by customer name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: '100%', marginBottom: '1em' }} />
            
           <Divider />

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.75rem 0' }}>
              <Button
                label={activeAdvancedFilterCount() > 0 ? `Advanced Filters (${activeAdvancedFilterCount()})` : 'Advanced Filters'}
                icon="pi pi-sliders-h"
                className="p-button-sm"
                onClick={() => setAdvancedFiltersOpen(true)}
                style={{ width: '100%' }}
              />
            </div>

            {activeAdvancedFilterCount() > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '0.5rem' }}>
                {deliveryOnly && <Tag severity="info" value="Delivery Only" rounded />}
                {notPaidOnly && <Tag severity="warning" value="Not Paid" rounded />}
                {!notPaidOnly &&
                  Object.entries(invoiceStatusFilters)
                    .filter(([, v]) => !!v)
                    .slice(0, 4)
                    .map(([k]) => <Tag key={k} severity="secondary" value={k.replaceAll('_', ' ')} rounded />)}
                {!notPaidOnly &&
                  Object.entries(invoiceStatusFilters).filter(([, v]) => !!v).length > 4 && (
                    <Tag severity="secondary" value={`+${Object.entries(invoiceStatusFilters).filter(([, v]) => !!v).length - 4} more`} rounded />
                  )}
              </div>
            )}


            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1em' }}>
              <div style={{ flex: '1' }}>
                <h5>Start Date</h5>
                <Calendar showIcon={true} value={dateRange.start}
                  onChange={(e) => {
                    const newStartDate = e.value;
                    if (newStartDate && !isNaN(newStartDate.getTime())) {
                      setDateRange({ ...dateRange, start: newStartDate });
                    } else {
                      toast.current.show({ severity: 'error', summary: 'Invalid Date', detail: 'Please select a valid start date.' });
                    }
                  }}
                  style={{ width: '100%', marginBottom: '1em' }} />
              </div>
              <div style={{ flex: '1' }}>
                <h5>End Date</h5>
                <Calendar showIcon={true} value={dateRange.end}
                  onChange={(e) => {
                    const newEndDate = e.value;
                    if (newEndDate && !isNaN(newEndDate.getTime())) {
                      setDateRange({ ...dateRange, end: newEndDate });
                    } else {
                      toast.current.show({ severity: 'error', summary: 'Invalid Date', detail: 'Please select a valid end date.' });
                    }
                  }}
                  style={{ width: '100%', marginBottom: '1em' }} />
              </div>
            </div>
             <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1em' }}>
            <div style={{ marginBottom: '1em' }}>
              <AnalyticsPage filteredOrders={filteredOrders} />
            </div>
            <div style={{ marginBottom: '1em' }}>
            <CustomerExplorerModal
              productProperties={productProperties}
              orderProperties={orderProperties}
              employees={employees}
            />
          </div>
          </div>
    
            <h3>Orders</h3>
            <div style={{ overflowY: 'auto', flexGrow: '1' }}>
              {
                isRefreshing ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <ProgressSpinner />
                  </div>
                ) : (
                  <DataView
                    value={filteredOrders}
                    itemTemplate={orderTemplate}
                    layout="list"
                    rows={5}
                    disabled={isSaving || isRefreshing}
                  />
                )
              }
            </div>
          </>
        )}
      </div>

      {/* Backdrop for Mobile Sidebar */}
      {isBackdropActive && (
        <div
          className={`sidebar-backdrop'}`}
          onClick={toggleLeftMenu}
        ></div>
      )}

      {/* Right Content Section */}
      <div
        className={`main-content ${isLeftMenuCollapsed ? 'expanded' : 'contracted'}`}
        style={{
          flex: isLeftMenuCollapsed ? '1' : '4',
          display: 'flex',
          flexDirection: 'column',
          padding: '1em',
          background: '#f7f7f7',
          overflow: 'hidden',
          transition: 'flex 0.3s ease'
        }}
      >
        <Menubar model={items} 
          style={{ marginBottom: '0.5em', backgroundColor: '#f9f9f9', borderRadius: '15px', paddingTop: '0.5em', paddingBottom: '0.5em' }}
          className="p-mb-2"
          start={<Button icon={isLeftMenuCollapsed ? 'pi pi-angle-double-right' : 'pi pi-angle-double-left'} onClick={toggleLeftMenu} className="p-button-text" />}
        />
        {selectedOrder ? (
          <OrderViewEdit
            order={selectedOrder}
            selectedOrderInvoice={selectedOrderInvoice}
            productProperties={productProperties}
            orderProperties={orderProperties}
            editable={editable}
            employees={employees}
            setEditable={setEditable}
            refreshOrders={refreshOrders}
            setSelectedOrder={setSelectedOrder}
            linkInvoiceToOrder={linkInvoiceToOrder}
            PotentialInvoices={selectedOrder ? <PotentialInvoices invoices={invoices} selectedOrder={selectedOrder} selectedOrderInvoice={selectedOrderInvoice} linkInvoiceToOrder={linkInvoiceToOrder} unlinkInvoiceFromOrder={unlinkInvoiceFromOrder} viewAll={viewAll}/> : null}
            isSaving={isSaving}
            setIsSaving={setIsSaving}
            customers={squareCustomers}
            filteredPhoneNumbers={filteredPhoneNumbers}
            filteredEmails={filteredEmails}
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: '#f7f7f7',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <h3>Please select or create an order</h3>
          </div>
        )}
      </div>
      <Dialog
        header="Advanced Filters"
        visible={advancedFiltersOpen}
        onHide={() => setAdvancedFiltersOpen(false)}
        modal
        style={{ width: '520px', maxWidth: '92vw' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontWeight: 800 }}>Invoice / Delivery</div>

            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-sm p-button-secondary"
              onClick={clearStatusFilters}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Checkbox
                inputId="filter-delivery-only"
                checked={deliveryOnly}
                onChange={(e) => setDeliveryOnly(!!e.checked)}
              />
              <label htmlFor="filter-delivery-only" style={{ cursor: 'pointer' }}>
                Delivery Only
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Checkbox
                inputId="filter-not-paid"
                checked={notPaidOnly}
                onChange={(e) => setNotPaidOnly(!!e.checked)}
              />
              <label htmlFor="filter-not-paid" style={{ cursor: 'pointer' }}>
                Not Paid (anything except Paid)
              </label>
            </div>
          </div>

          <Divider />

          <div style={{ opacity: notPaidOnly ? 0.55 : 1 }}>
            <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>
              Invoice Status (OR)
              <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 8 }}>
                {notPaidOnly ? '(disabled: Not Paid is active)' : ''}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {INVOICE_STATUS_OPTIONS.map((opt) => (
                <div key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Checkbox
                    inputId={`inv-${opt.key}`}
                    disabled={notPaidOnly}
                    checked={!!invoiceStatusFilters[opt.key]}
                    onChange={(e) =>
                      setInvoiceStatusFilters((prev) => ({
                        ...prev,
                        [opt.key]: !!e.checked,
                      }))
                    }
                  />
                  <label htmlFor={`inv-${opt.key}`} style={{ cursor: notPaidOnly ? 'not-allowed' : 'pointer' }}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '0.75rem', opacity: 0.75, fontSize: '0.85rem' }}>
              Tip: “Partially Paid” + “Delivery Only” = Partially Paid & Delivery, etc.
            </div>
          </div>

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button
              label="Done"
              icon="pi pi-check"
              className="p-button-sm"
              onClick={() => setAdvancedFiltersOpen(false)}
            />
          </div>
        </div>
      </Dialog>

      <Toast ref={toast} />
    </div>
  );



};

export default Orders;

